import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport, FixedSizeVirtualScrollStrategy, VIRTUAL_SCROLL_STRATEGY } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectorRef, Directive, HostBinding, Inject, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { PaginationAdapter } from '@red/data-access';
import { IQuerySearch } from '@shared/data-access/interfaces';
import { concat } from 'lodash-es';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  animationFrameScheduler,
  asapScheduler,
  auditTime,
  debounceTime,
  filter,
  startWith,
  takeUntil,
  tap,
} from 'rxjs';

const SCROLL_SCHEDULER = typeof requestAnimationFrame !== 'undefined' ? animationFrameScheduler : asapScheduler;

export class AutocompleteVirtualScrollStrategy extends FixedSizeVirtualScrollStrategy {
  constructor() {
    super(42, 128, 208);
  }
}

@Directive({
  selector: 'cdk-virtual-scroll-viewport[redAutocompleteScrollController]',
  exportAs: 'autocompleteScrollController',
  providers: [{ provide: VIRTUAL_SCROLL_STRATEGY, useClass: AutocompleteVirtualScrollStrategy }, CdkFixedSizeVirtualScroll],
})
export class AutocompleteScrollControllerDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() limit = 10;
  @Input() searchWith!: (filters: IQuerySearch) => Observable<any[] | PaginationAdapter<any>>;

  page = 1;
  total = 0;
  length = 0;
  multiple = 4;
  loading!: boolean;

  @HostBinding('style.height.px') get height() {
    return this._fixedSize._itemSize * this.multiple;
  }

  waitToFetch!: Subscription;
  searchCtrl = new FormControl('');
  results = new BehaviorSubject<any[]>([]);

  private _onDestroy = new Subject<void>();

  constructor(
    private _ngZone: NgZone,
    private _cdr: ChangeDetectorRef,

    @Inject(MatAutocomplete) private _matAutocomplete: MatAutocomplete,
    @Inject(VIRTUAL_SCROLL_STRATEGY) private _fixedSize: CdkFixedSizeVirtualScroll,
    @Inject(CdkVirtualScrollViewport) private _scrollViewport: CdkVirtualScrollViewport
  ) {}

  ngAfterViewInit(): void {
    this._ngZone.runOutsideAngular(() =>
      Promise.resolve().then(() => {
        this._scrollViewport.scrollable
          .elementScrolled()
          .pipe(startWith(null), auditTime(0, SCROLL_SCHEDULER), takeUntil(this._onDestroy))
          .subscribe(() => {
            this.onContentScroll();
          });
      })
    );
  }

  onContentScroll(): void {
    const renderedRange = this._scrollViewport.getRenderedRange();
    const newRange = { start: renderedRange.start, end: renderedRange.end };
    const dataLength = this._scrollViewport.getDataLength();

    if (newRange.end < dataLength || this.length >= this.total) return;

    const currentPage = Math.ceil(newRange.end / this.limit);
    if (currentPage === this.page) {
      this.page += 1;
      this.triggerSearch();
    }
  }

  ngOnInit(): void {
    this.onSearchChange()
      .pipe(
        filter(value => {
          if (value && value.length) {
            return value.length > 0;
          }

          return false;
        }),
        tap(() => {
          this.page = 1;
          this.results.next([]);
        }),
        takeUntil(this._onDestroy)
      )
      .subscribe(() => {
        this.triggerSearch();
      });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  onSearchChange(): Observable<string | null> {
    return this.searchCtrl.valueChanges.pipe(debounceTime(300));
  }

  triggerSearch(): void {
    this.loading = true;
    if (this.waitToFetch && !this.waitToFetch.closed) {
      this.waitToFetch.unsubscribe();
    }

    const filters = { key: this.searchCtrl.getRawValue(), limit: this.limit, page: this.page };

    this.waitToFetch = this.searchWith(filters).subscribe(data => {
      const newItems = Array.isArray(data) ? data : data.results;
      const list = concat(this.results.getValue(), newItems);
      this.length = list.length;
      this.length === 0 ? (this.multiple = 0) : (this.multiple = 4);
      this.total = Array.isArray(data) ? this.length : data.pagination.total;
      this.results.next(list);
      this.loading = false;
    });
  }
}
